import * as React from 'react';

import {
  PlanType,
  SubscriptionStatus,
} from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useAtom } from 'jotai';

import { languageAtom } from '~shared/atoms';
import { Link } from '~shared/components/Link';
import { calculateSubscriptionState } from '~teacher/components/Account/helpers';

import { Subscription } from '../../../../api/subscription';

export function TeamSubscriptionPlanLoading() {
  return (
    <Plan>
      <Plan.Header>{translate('Plan Details')}</Plan.Header>
      <Plan.Row>
        <Plan.RowLabel>{translate('Plan Type')} </Plan.RowLabel>
        <Plan.RowValue>Loading</Plan.RowValue>
      </Plan.Row>
      <Plan.Row>
        <Plan.RowLabel>{translate('Billing Period')} </Plan.RowLabel>
        <Plan.RowValue>Loading</Plan.RowValue>
      </Plan.Row>
      <Plan.Row>Loading</Plan.Row>
      <Plan.Row>Loading</Plan.Row>
    </Plan>
  );
}

export interface TeamSubscriptionPlanDataProps {
  plan: PlanType;
  planDuration: string;
  endDate: string;
  cancelAtEndDate: boolean;
  status: SubscriptionStatus;
  totalSeats: number;
  seatsAvailable: number;
  teamId: string;
}

export function TeamSubscriptionPlanData({
  plan,
  planDuration,
  status,
  endDate,
  cancelAtEndDate,
  totalSeats,
  seatsAvailable,
  teamId,
}: TeamSubscriptionPlanDataProps) {
  const [language] = useAtom(languageAtom);

  return (
    <Plan>
      <Plan.Header>{translate('Plan Details')}</Plan.Header>
      <Plan.Row>
        <Plan.RowLabel>{translate('Plan Type')} </Plan.RowLabel>
        <Plan.RowValue>{plan}</Plan.RowValue>
      </Plan.Row>
      <Plan.Row>
        <Plan.RowLabel>{translate('Billing Period')}</Plan.RowLabel>{' '}
        <Plan.RowValue>{planDuration}</Plan.RowValue>
      </Plan.Row>
      <Plan.Row>
        <Plan.RowLabel>
          {subscriptionStatus({ endDate, cancelAtEndDate, status, plan })}
        </Plan.RowLabel>{' '}
        <Plan.RowValue>
          {new Date(endDate).toLocaleDateString(language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </Plan.RowValue>
      </Plan.Row>

      {
        // If there isn't more than 1 seat, this message doesn't make any sense to show.
        // Also hide if they are expired
        totalSeats > 1 && !['Expired'].includes(status) ? (
          <Plan.Row>
            {translate('{0} Seats Purchased ({1} available)').format(
              totalSeats,
              seatsAvailable
            )}
          </Plan.Row>
        ) : null
      }

      {plan === PlanType.Essentials && !['Expired'].includes(status) ? (
        <Plan.Row>
          {translate('Upgrade to Socrative PRO for more seats.')}{' '}
          <Link
            href="https://www.socrative.com/plans/"
            target="_blank"
            className="font-600 text-cyan-700"
            includeCommonStyles
          >
            {translate('Learn More')}
          </Link>
        </Plan.Row>
      ) : null}

      {['Expired'].includes(status) || plan === PlanType.Free ? (
        <Plan.Row>
          {translate(
            'Upgrade to Socrative PRO for unlimited quizzes, multiple rooms, roster imports, and more.'
          )}{' '}
          <Link
            href="https://www.socrative.com/plans/"
            target="_blank"
            className="font-600 text-cyan-700"
            includeCommonStyles
          >
            {translate('Learn More')}
          </Link>
        </Plan.Row>
      ) : null}

      <Plan.TeamId>
        {translate('Team ID:')} {teamId}
      </Plan.TeamId>
    </Plan>
  );
}

export function TeamSubscriptionPlanFree({ teamId }: { teamId: string }) {
  return (
    <Plan>
      <Plan.Header>{translate('Plan Details')}</Plan.Header>
      <Plan.Row>
        <Plan.RowLabel>{translate('Plan Type')} </Plan.RowLabel>
        <Plan.RowValue>Free</Plan.RowValue>
      </Plan.Row>
      <Plan.Row>
        {translate(
          'Upgrade to Socrative PRO for unlimited quizzes, multiple rooms, roster imports, and more.'
        )}{' '}
        <Link
          href="https://www.socrative.com/plans/"
          target="_blank"
          className="font-600 text-cyan-700"
          includeCommonStyles
        >
          {translate('Learn More')}
        </Link>
      </Plan.Row>
      <Plan.TeamId>
        {translate('Team ID:')} {teamId}
      </Plan.TeamId>
    </Plan>
  );
}

export function Plan({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-4 mb-4 sm:mb-0">{children}</div>;
}

type SubscriptionStatusParams = Pick<
  Subscription,
  'endDate' | 'cancelAtEndDate' | 'status' | 'plan'
>;

export function subscriptionStatus({
  endDate,
  cancelAtEndDate,
  status,
  plan,
}: SubscriptionStatusParams) {
  const subscriptionState = calculateSubscriptionState({
    status,
    endDate,
    cancelAtEndDate,
    plan,
  });

  switch (subscriptionState) {
    case 'None':
      return null;
    case 'Expired':
    case 'PastDue':
      return translate('Expired');
    case 'CancellingTrial':
    case 'Cancelling':
      return translate('Cancelling');
    case 'Trial':
    case 'Valid':
      return translate('Renews');
  }
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="font-sans leading-8 text-base">{children}</div>;
}

function RowLabel({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function RowValue({ children }: { children: React.ReactNode }) {
  return <span className="font-600 capitalize">{children}</span>;
}

function TeamId({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-sans leading-tight text-xs text-grey-900">
      {children}
    </div>
  );
}
function Header({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-display font-700 text-base leading-8">{children} </div>
  );
}

Plan.Header = Header;
Plan.Row = Row;
Plan.RowLabel = RowLabel;
Plan.RowValue = RowValue;
Plan.TeamId = TeamId;
